import { getCurrentInstance } from '@vue/composition-api'
import { ADMIN_CREDENTIALS_STORAGE_KEY, canAccessFinancialMenu } from '@/constants/auth'
import { getCredentials } from '@/helpers/credentials'

const canViewFixedRoleItem = item => {
  if (!item.fixedFinancialMenuRole) return true

  const credentials = getCredentials(ADMIN_CREDENTIALS_STORAGE_KEY)
  if (canAccessFinancialMenu(credentials?.user?.user_role_id)) return true

  // Perfis consultivos (ex.: Intelligence - Consultor Financeiro) não estão na lista
  // fixa de roles do menu financeiro, mas acessam em modo leitura via ability (CASL).
  return item.resource ? can('read', item.resource) : false
}

/**
 * Returns ability result if ACL is configured or else just return true
 * Useful if you don't know if ACL is configured or not
 * Used in @core files to handle absence of ACL without errors
 * @param {String} action CASL Actions // https://casl.js.org/v4/en/guide/intro#basics
 * @param {String} subject CASL Subject // https://casl.js.org/v4/en/guide/intro#basics
 */
export const can = (action, subject) => {
  const vm = getCurrentInstance().proxy
  return vm.$can ? vm.$can(action, subject) : true
}

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */
export const canViewVerticalNavMenuLink = item => {
  if (item.fixedFinancialMenuRole) return canViewFixedRoleItem(item)
  if (item.anyOfResources) return item.anyOfResources.some(resource => can(item.action, resource))
  return can(item.action, item.resource)
}

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource & Hide group if all of it's children are hidden
 * @param {Object} item navigation object item
 */
// eslint-disable-next-line arrow-body-style
export const canViewVerticalNavMenuGroup = item => {
  // ! This same logic is used in canViewHorizontalNavMenuGroup and canViewHorizontalNavMenuHeaderGroup. So make sure to update logic in them as well
  const hasAnyVisibleChild = item.children.some(i => {
    if (i.fixedFinancialMenuRole) return canViewFixedRoleItem(i)
    if (i.anyOfResources) return i.anyOfResources.some(resource => can(i.action, resource))
    return can(i.action, i.resource)
  })

  if (item.fixedFinancialMenuRole) {
    return canViewFixedRoleItem(item) && hasAnyVisibleChild
  }

  // If resource and action is defined in item => Return based on children visibility (Hide group if no child is visible)
  // Else check for ability using provided resource and action along with checking if has any visible child
  if (!(item.action && item.resource)) {
    return hasAnyVisibleChild
  }
  return can(item.action, item.resource) && hasAnyVisibleChild
}

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */
export const canViewVerticalNavMenuHeader = item => can(item.action, item.resource)

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */
export const canViewHorizontalNavMenuLink = item => {
  if (item.fixedFinancialMenuRole) return canViewFixedRoleItem(item)
  return can(item.action, item.resource)
}

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */
export const canViewHorizontalNavMenuHeaderLink = item => can(item.action, item.resource)

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource & Hide group if all of it's children are hidden
 * @param {Object} item navigation object item
 */
// eslint-disable-next-line arrow-body-style
export const canViewHorizontalNavMenuGroup = item => {
  // ? Same logic as canViewVerticalNavMenuGroup
  const hasAnyVisibleChild = item.children.some(i => (i.fixedFinancialMenuRole ? canViewFixedRoleItem(i) : can(i.action, i.resource)))

  if (item.fixedFinancialMenuRole) {
    return canViewFixedRoleItem(item) && hasAnyVisibleChild
  }

  // If resource and action is defined in item => Return based on children visibility (Hide group if no child is visible)
  // Else check for ability using provided resource and action along with checking if has any visible child
  if (!(item.action && item.resource)) {
    return hasAnyVisibleChild
  }
  return can(item.action, item.resource) && hasAnyVisibleChild
}

// eslint-disable-next-line arrow-body-style
export const canViewHorizontalNavMenuHeaderGroup = item => {
  // ? Same logic as canViewVerticalNavMenuGroup but with extra content

  // eslint-disable-next-line arrow-body-style
  const hasAnyVisibleChild = item.children.some(grpOrItem => {
    // If it have children => It's grp
    // Call ACL function based on grp/link
    return grpOrItem.children ? canViewHorizontalNavMenuGroup(grpOrItem) : canViewHorizontalNavMenuLink(grpOrItem)
  })

  // If resource and action is defined in item => Return based on children visibility (Hide group if no child is visible)
  // Else check for ability using provided resource and action along with checking if has any visible child
  if (!(item.action && item.resource)) {
    return hasAnyVisibleChild
  }
  return can(item.action, item.resource) && hasAnyVisibleChild
}
